import { buildTextParameterTypeHints } from '../../revit/wizardParameterDataTypes';
import {
  buildTolerantProjectParameterMutationBatches,
  getNewProjectParameterNames,
} from '../../revit/projectParameterMutationPlan';
import { sanitizeRevitParameterNames } from '../../revitParameterNames';
import { areAdditionalCategoryProjectParametersAvailable } from './additionalCategoryPlan';
import { fetchAvailableProjectParameters } from './availability';
import { rememberAvailableProjectParameters } from './availableProjectParameterCache';
import { buildConnectorUndoPlan } from './connectorPlan';
import { buildScopedBindingCompletionNotification, notifyParameterAddition } from './feedback';
import { projectChangedResult } from './operationIdentity';
import {
  type AdditionalCategoryAdditionOperation,
  type ParameterAdditionOptions,
  type ParameterAdditionResult,
} from './models';
import { type ParameterAdditionCore } from './useParameterAdditionCore';
import { type ParameterMutationActions } from './useParameterMutationActions';

export async function runAdditionalCategoryProjectAddition(
  context:
    & Pick<
      ParameterAdditionCore,
      | 'additionalCategoriesConfig'
      | 'didProjectChange'
      | 'setAdditionalCategoryParameterUndoPlan'
      | 'showInfo'
      | 'showSuccess'
      | 'showWarning'
    >
    & Pick<ParameterMutationActions, 'addAsProjectParameters'>,
  operations: AdditionalCategoryAdditionOperation[],
  targetParameterNames: string[],
  requestedProjectKey: string,
  options: ParameterAdditionOptions,
): Promise<ParameterAdditionResult> {
  const {
    addAsProjectParameters,
    additionalCategoriesConfig,
    didProjectChange,
    setAdditionalCategoryParameterUndoPlan,
    showInfo,
    showSuccess,
    showWarning,
  } = context;

  const availableBeforeDescriptors = await fetchAvailableProjectParameters({
    cacheScopeKey: requestedProjectKey,
    force: true,
  });

  if (didProjectChange(requestedProjectKey)) {
    const result = projectChangedResult();
    notifyParameterAddition(
      options,
      showWarning,
      result.error ?? 'The active Revit project changed.',
    );
    return result;
  }

  if (
    areAdditionalCategoryProjectParametersAvailable(
      additionalCategoriesConfig,
      availableBeforeDescriptors,
    )
  ) {
    notifyParameterAddition(
      options,
      showInfo,
      'All additional-category project parameters already match the selected mode. No new parameters were added.',
    );
    return {
      success: true,
      addedCount: 0,
      existingCount: targetParameterNames.length,
      queued: false,
      requestedCount: targetParameterNames.length,
      undoAvailable: false,
    };
  }

  const projectUndoParameterNames: string[] = [];

  const results: ParameterAdditionResult[] = [];

  for (
    const operation of operations.filter((operation) => operation.destination === 'project')
  ) {
    const parameterTypeHints = buildTextParameterTypeHints(
      operation.parameterNames,
    );
    const mutationBatches = buildTolerantProjectParameterMutationBatches(
      operation.parameterNames,
      operation.bindingKind,
      operation.categories ?? [],
      availableBeforeDescriptors,
      parameterTypeHints,
    );

    for (const batch of mutationBatches) {
      projectUndoParameterNames.push(
        ...getNewProjectParameterNames(
          batch.parameterNames,
          availableBeforeDescriptors,
        ),
      );
      const result = await addAsProjectParameters(
        batch.parameterNames,
        {
          categories: operation.categories,
          bindingKind: batch.bindingKind,
          parameterTypeHints,
        },
      );
      if (didProjectChange(requestedProjectKey)) {
        const changedResult = projectChangedResult();
        notifyParameterAddition(
          options,
          showWarning,
          changedResult.error ??
            'The active Revit project changed.',
        );
        return changedResult;
      }
      if (!result.success) {
        notifyParameterAddition(
          options,
          showWarning,
          result.error ||
            'Some additional-category project parameters could not be added.',
        );
        return result;
      }
      if (
        (result.failedCount || 0) === 0 &&
        (result.conflictingCount || 0) === 0
      ) {
        rememberAvailableProjectParameters(
          batch.parameterNames,
          batch.bindingKind,
          requestedProjectKey,
          operation.categories,
          parameterTypeHints,
        );
      }
      results.push(result);
    }
  }

  if (results.length === 0) {
    notifyParameterAddition(
      options,
      showInfo,
      'All additional-category project parameters already match the selected mode. No new parameters were added.',
    );
    return {
      success: true,
      addedCount: 0,
      existingCount: targetParameterNames.length,
      queued: false,
      requestedCount: targetParameterNames.length,
      undoAvailable: false,
    };
  }

  const totalAddedCount = results.reduce(
    (sum, result) => sum + (result.addedCount || 0),
    0,
  );

  const totalUpdatedCount = results.reduce(
    (sum, result) => sum + (result.updatedCount || 0),
    0,
  );

  const totalExistingCount = results.reduce(
    (sum, result) => sum + (result.existingCount || 0),
    0,
  );

  const totalFailedCount = results.reduce(
    (sum, result) => sum + (result.failedCount || 0),
    0,
  );

  const totalConflictingCount = results.reduce(
    (sum, result) => sum + (result.conflictingCount || 0),
    0,
  );

  const additionalCategoryUndoPlan = totalFailedCount === 0 && totalConflictingCount === 0
    ? buildConnectorUndoPlan(
      [],
      [],
      [],
      sanitizeRevitParameterNames(projectUndoParameterNames),
    )
    : null;

  if (didProjectChange(requestedProjectKey)) {
    const changedResult = projectChangedResult();
    notifyParameterAddition(
      options,
      showWarning,
      changedResult.error ?? 'The active Revit project changed.',
    );
    return changedResult;
  }

  if (additionalCategoryUndoPlan) {
    setAdditionalCategoryParameterUndoPlan({
      ...additionalCategoryUndoPlan,
      projectKey: requestedProjectKey,
    });
  }

  const notification = buildScopedBindingCompletionNotification({
    requestedCount: targetParameterNames.length,
    addedOrUpdatedCount: totalAddedCount + totalUpdatedCount,
    existingCount: totalExistingCount,
    conflictingCount: totalConflictingCount,
    failedCount: totalFailedCount,
  });

  if (notification.severity === 'warning') {
    notifyParameterAddition(
      options,
      showWarning,
      notification.message,
    );
  } else if (notification.severity === 'info') {
    notifyParameterAddition(
      options,
      showInfo,
      notification.message,
    );
  } else {
    notifyParameterAddition(
      options,
      showSuccess,
      additionalCategoryUndoPlan
        ? `${notification.message} Undo is available on this step for the parameter setup changes.`
        : notification.message,
    );
  }

  return {
    success: totalFailedCount === 0 && totalConflictingCount === 0,
    addedCount: totalAddedCount,
    updatedCount: totalUpdatedCount,
    existingCount: totalExistingCount,
    failedCount: totalFailedCount,
    conflictingCount: totalConflictingCount,
    queued: results.some((result) => result.queued),
    requestedCount: targetParameterNames.length,
    undoAvailable: additionalCategoryUndoPlan !== null,
    failedParameters: results.flatMap((result) => result.failedParameters ?? []),
    conflictingParameters: results.flatMap((result) => result.conflictingParameters ?? []),
    failureMessages: results.flatMap((result) => result.failureMessages ?? []),
  };
}
